import React, { useEffect, useLayoutEffect, useState } from "react";
import MainContainer from "../../../components/container/MainContainer";
import PageTitle from "../../../components/titles/PageTitle";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProject } from "../../../features/projectsSlice";
import Loading from "../../../components/loading/Loading";
import TextContainer from "../../../components/text/TextContainer";
import Modal from "../../../components/modal/Modal";
import { openModal } from "../../../features/modalSLice";
import AddProvincialApproval from "./AddProvincialApproval";
import AddProvincialRejection from "./AddProvincialRejection";
import LinkTextContainer from "../../../components/text/LinkTextContainer";
import PDFComponent from "../../../components/pdf/PDFComponent";
import DisplayComments from "../../../components/text/DisplayComments";
import { useAuthorization } from "../../../hooks/useAuthorization";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const ProvincialReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthorized } = useAuthorization();
  const project = useSelector((state) => state.projects.selected); // List of ongoing projects
  const modalContent = useSelector((state) => state.modal.content);
  const loading = useSelector((state) => state.projects.load.getProject);
  const [data, setData] = useState([]);

  const openApproveProvincial = () => {
    dispatch(
      openModal(<AddProvincialApproval id={project.id} name={project.name} />)
    );
  };

  const openRejectProvincial = () => {
    dispatch(
      openModal(<AddProvincialRejection id={project.id} name={project.name} />)
    );
  };

  const openTotalRejectProvincial = () => {
    dispatch(
      openModal(
        <AddProvincialRejection reject id={project.id} name={project.name} />
      )
    );
  };

  useEffect(() => {
    if (id) {
      dispatch(getProject(id));
    }
  }, []);

  useLayoutEffect(() => {
    if (
      (project.id && project?.stage?.step_number < 5) ||
      (project.id && project?.stage?.step_number > 6)
    ) {
      return navigate("/requests/provincial-approvals", { replace: true });
    }
  }, [project, navigate]);

  useEffect(() => {
    if (project.id) {
      setData([
        {
          heading: project.name,
          content: [],
        },
        {
          content: [
            `
            <q>Community: ${project.community}</q>
            <q>Address 1: ${project.community_address1}</q>
            <q>Address 2: ${project.community_address2}</q>
            <q>Project Location: ${project.project_location}</q>
            <q>Country: ${project.country}</q>
            <q>Province: ${project.province}</q>
            <q>Community Contact Name: ${project.creator?.first_name} ${
              project.creator.other_names
                ? project.creator.other_names + " "
                : ""
            }${project.creator?.last_name}</q>
              <q>Community Contact Email: ${project.creator.email}</q>
              <q>Community Contact Phone: ${project.creator.phone || "NA"}</q>
            `,
          ],
        },
        {
          heading: "Analysis",
          content: [project.analysis],
        },
        { heading: "Problem Statement", content: [project.problem_statement] },
        {
          heading: "Proposed (Idea) Solution",
          content: [project.proposed_solution],
        },
      ]);
    }
  }, [project]);

  if (loading === loadStatus.loading) {
    return <Loading />;
  }

  if (loading === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <Modal>{modalContent}</Modal>
      <div className="w-full max-w-5xl mx-auto text-text-1/70">
        <div className="sm:flex justify-between items-center py-4">
          <PageTitle title="Provincial - Request Review & Approval" />
          <PDFComponent
            title={project.name + ", Provincial Request Review"}
            data={data}
          />
        </div>
        {project.id && (
          <>
            <div className="mx-auto max-w-5xl w-full bg-content shadow-md px-5 py-6 flex flex-col gap-0.5">
              <h5 className="summary-p text-lg mb-4">
                <span className=" ">{project.name}</span>
              </h5>
              <TextContainer
                label="Name of Applicant"
                content={`${project.creator.first_name} ${project.creator.last_name}`}
              />
              <TextContainer label="Community" content={project.community} />
              <TextContainer
                label="Address 1"
                content={project.community_address1}
              />
              <TextContainer
                label="Address 2"
                content={project.community_address2}
              />
              <TextContainer label="Province" content={project.province} />
              <TextContainer
                label="Address Project Location"
                content={project.project_location}
              />
              <TextContainer label="Email" content={project.creator.email} />
              <TextContainer label="Contact" content={project.creator.phone} />

              {/* Display Comment */}
              <DisplayComments comments={project.comments} />
              {/* Body */}

              <TextContainer
                label="Analysis"
                content={project.analysis}
                isHTML
              />
              <TextContainer
                label="Problem Statement"
                content={project.problem_statement}
                isHTML
              />
              <TextContainer
                label="Proposed (Idea) Solution"
                content={project.proposed_solution}
                isHTML
              />
              {/* The Documents */}
              <LinkTextContainer
                document={project.supporting_docs}
                role="Applicant"
                title="Supporting Documents"
              />
              <LinkTextContainer
                document={project.community_council_minute}
                role="Community"
                title="Community Council Minutes"
              />
            </div>
            <div className="max-w-5xl w-full mx-auto">
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/requests/provincial-approvals");
                  }}
                  className="cancel-rss-btn"
                >
                  Back
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={openTotalRejectProvincial}
                    disabled={!isAuthorized("provincial_council", "update")}
                    className="delete-rss-btn"
                  >
                    Disapprove
                  </button>
                  <button
                    type="button"
                    onClick={openRejectProvincial}
                    disabled={!isAuthorized("provincial_council", "update")}
                    className="add-rss-btn"
                  >
                    Hold
                  </button>
                  <button
                    type="button"
                    onClick={openApproveProvincial}
                    disabled={!isAuthorized("provincial_council", "update")}
                    className="add-rss-btn"
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default ProvincialReview;
